import React, { useState } from 'react';
import { gql } from '@apollo/client';
import styles from './EmailPermissions.module.scss';

import client from 'store/apollo';
import {
  Button,
  Checkbox,
  Heading,
  Link,
  Text,
  createToast,
  Toast,
  dismissToast,
  ToastButton,
} from 'components/core';
import { Stack } from 'components/layout';
import { PageContainer } from 'components/shared';

const GRANT_MAILING_LIST_CONSENT = gql`
  mutation grantMailingListConsent($signedSource: String!) {
    saveConsent: grantMailingListConsent(
      input: { signedSource: $signedSource }
    ) {
      success
      errors {
        code
        message
      }
    }
  }
`;

const DENY_MAILING_LIST_CONSENT = gql`
  mutation denyMailingListConsent($signedSource: String!) {
    saveConsent: denyMailingListConsent(
      input: { signedSource: $signedSource }
    ) {
      success
      errors {
        code
        message
      }
    }
  }
`;

const consentMutate = (
  mailingListConsent: boolean,
  signedSource: string
): Promise<any> => {
  const mutation = mailingListConsent
    ? GRANT_MAILING_LIST_CONSENT
    : DENY_MAILING_LIST_CONSENT;

  return client
    .mutate({ mutation, variables: { signedSource } })
    .then((result) => {
      return result.data.saveConsent;
    });
};

type Props = {
  signedSource: string;
  mailingListConsent: boolean;
  mutate?: (mailingListConsent: boolean, signedSource: string) => Promise<any>;
};

function EmailPermissions(props: Props) {
  const { signedSource, mutate = consentMutate } = props;

  const [state, setState] = useState({
    mailingListConsent: props.mailingListConsent,
    saveStatus: '',
  });

  const { saveStatus, mailingListConsent } = state;

  function onCheckboxUpdate(val: any) {
    setState({
      saveStatus: '',
      mailingListConsent: val,
    });
  }

  function save(mailingListConsent: any, triggerElement: HTMLElement | null) {
    setState((prev) => {
      return { ...prev, saveStatus: 'in-progress' };
    });

    mutate(mailingListConsent, signedSource).then((data) => {
      const saveStatus = data.success ? 'success' : 'error';
      setState((prev) => {
        return {
          ...prev,
          saveStatus,
        };
      });

      if (saveStatus === 'success') {
        dismissToast();

        setTimeout(() => {
          createToast(
            <Toast className="saved-changes-toast" type="success">
              Your changes have been saved
            </Toast>
          );
        }, 500);
      }

      if (saveStatus === 'error') {
        dismissToast();

        const errors = data.errors;
        const error = errors[0];
        const code = error?.code || '';

        let message =
          "Oh no! We're experiencing some technical difficulties right now, please try again later";
        let buttonText = 'Reload';

        if (['unauthorized', 'incorrectly_signed_argument'].includes(code)) {
          message = 'Please log in again to access this page';
          buttonText = 'Login';
        }

        setTimeout(() => {
          createToast(
            <Toast
              className="saved-changes-toast"
              type="danger"
              focusOnAppear
              restoreFocusTo={triggerElement}
            >
              {message}{' '}
              <ToastButton to={window.location.href}>{buttonText}</ToastButton>
            </Toast>
          );
        }, 500);
      }
    });
  }

  return (
    <PageContainer aria-live="polite">
      <Heading type={'h3'}>Email notifications</Heading>
      <Stack spacing={2} className={styles.textWrapper}>
        <Checkbox
          disabled={saveStatus === 'in-progress'}
          checked={mailingListConsent}
          onChange={(e) => onCheckboxUpdate(e.target.checked)}
        >
          I&apos;d like to receive emails about product enhancements, systematic
          review training and best practices
        </Checkbox>
        <Text variant="weak" size={'xs'}>
          We use Pendo as our platform for delivering these emails. You can find
          out more in{' '}
          <Link
            to="https://www.pendo.io/legal/privacy-policy/"
            title="Pendo's privacy policy"
            external={true}
            variant="primary"
          >
            Pendo&apos;s privacy policy
          </Link>
        </Text>
      </Stack>
      <Button
        onClick={(event: React.MouseEvent<HTMLButtonElement>) =>
          save(mailingListConsent, event.currentTarget)
        }
        isLoading={saveStatus === 'in-progress'}
        size="md"
        type={'brand'}
      >
        Save
      </Button>
    </PageContainer>
  );
}

export default EmailPermissions;
